// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import TypeVariablesEdit from '../forms/sensortypevariables/Edit';

import { typeSensorVariable, TypeSensorVariableListDO } from '../../requests';

interface Props {
  rowId: number;
}

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: TypeSensorVariableListDO[];
}

export default class SensorTypeVariables extends React.Component<Props, State> {
  private tabId: any;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      add: true,
      del: true,
      toggleItem: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const { rowId } = this.props;

    this.loadingStart();
    typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId: rowId })
      ?.then((resp: TypeSensorVariableListDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'STVE';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: rowId ? `Variable (Sensor-Type) Edit: ${rowId}` : 'Variable (Sensor-Type) New',
      component: TypeVariablesEdit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'TYPES VARIABLES EDIT',
        title: 'Types Variables Edit',
        notify: this.notify,
        typeSensor: {
          id: this.props.rowId,
        },
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (value: TypeSensorVariableListDO): void => {
    window.store.dispatch(removeTab('STVE'));
    const rowId: number = value?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: TypeSensorVariableListDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      typeSensorVariable('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      order: 'Order',
      variableName: 'Variable',
      bytes: 'Bytes',
      formula: 'Formula',
      discardMin: 'Discard min',
      discardMax: 'Discard max',
      adjustMin: 'Adjust min',
      adjustMax: 'Adjust max',
      variableUnit: 'Unit',
    };
    const columnsToHide: string[] = [
      'variableId',
      'typeSensorName',
      'typeSensorId'
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      order: { textAlign: 'right' },
      bytes: { textAlign: 'right' },
      formula: { textAlign: 'left' },
      discardMin: { textAlign: 'right' },
      discardMax: { textAlign: 'right' },
      adjustMin: { textAlign: 'right' },
      adjustMax: { textAlign: 'right' },
      variableName: { textAlign: 'left' },
      variableUnit: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
